<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">{{ userRole === 'Salesperson' ? 'VIEW SO TRACKER' : 'MANAGE SO TRACKER' }}</h1>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64"/>
              </v-overlay>

              <!-- Locked Status Alert -->
              <v-alert
                v-if="lockAll"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                <v-icon slot="prepend">mdi-lock</v-icon>
                This SO is {{ originalStatus.toLowerCase() }} and locked from edits
              </v-alert>

              <!-- Section Header -->
              <div class="section-header mb-6">
                <h2 class="section-title">SO TRACKER DETAILS</h2>
                <div class="section-divider"></div>
              </div>

              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Top Row: SO Number, Customer -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field 
                      v-model="salesOrderNumber" 
                      label="SO Number" 
                      dense outlined 
                      prepend-inner-icon="mdi-note-text"
                      :rules="[rules.required]"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="customerName"
                      :items="customerOptions" 
                      item-text="companyName" 
                      item-value="docId"
                      label="Customer Name" 
                      dense outlined 
                      prepend-inner-icon="mdi-account-group"
                      :rules="[rules.required]"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Personnel Row -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="engineer" 
                      :items="engineerOptions" 
                      item-text="fullName" 
                      return-object
                      label="Engineer" 
                      dense outlined 
                      prepend-inner-icon="mdi-account"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select 
                      v-model="salesperson" 
                      :items="salespersonOptions" 
                      item-text="fullName" 
                      return-object
                      label="Salesperson" 
                      dense outlined 
                      prepend-inner-icon="mdi-account"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Dates and Status Row -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-menu ref="orderDateMenu" v-model="orderDateMenu" :close-on-content-click="false"
                            transition="scale-transition" offset-y min-width="auto">
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="orderPlacedDate" 
                          label="Order Placed Date" 
                          dense outlined readonly
                          prepend-inner-icon="mdi-calendar" 
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]" 
                          :disabled="userRole === 'Salesperson' || lockAll"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker v-model="orderPlacedDate" @input="orderDateMenu=false"/>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-select
                      v-model="method" 
                      :items="methodOptions" 
                      label="Method" 
                      dense outlined
                      prepend-inner-icon="mdi-truck-delivery" 
                      :rules="[rules.required]"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-select
                      v-model="status"
                      :items="statusOptions"
                      label="Status"
                      dense outlined
                      prepend-inner-icon="mdi-information"
                      :rules="[rules.required]"
                      :readonly="userRole === 'Salesperson' || lockAll"
                      class="nexus-input"
                    />
                    <v-alert
                      v-if="isBeingSetToComplete && !lockAll"
                      type="warning"
                      dense
                      class="mt-2"
                    >
                      <v-icon slot="prepend" small>mdi-lock-alert</v-icon>
                      Warning: Setting to Complete will lock this SO from further edits
                    </v-alert>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-menu ref="arrivalDateMenu" v-model="arrivalDateMenu" :close-on-content-click="false"
                            transition="scale-transition" offset-y min-width="auto">
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="arrivalDate" 
                          label="Expected Arrival Date" 
                          dense outlined readonly
                          prepend-inner-icon="mdi-calendar" 
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]" 
                          :disabled="userRole === 'Salesperson' || lockAll"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker v-model="arrivalDate" @input="arrivalDateMenu=false"/>
                    </v-menu>
                  </v-col>
                </v-row>

                <!-- PDF Upload Section (Procurement Only) -->
                <v-row v-if="userRole !== 'Salesperson'">
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">SO PDF DOCUMENT</h3>
                      <div class="section-divider"></div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="pdfFile"
                      label="Upload SO PDF"
                      accept=".pdf"
                      prepend-icon="mdi-file-pdf-box"
                      outlined
                      dense
                      :disabled="lockAll"
                      @change="handlePDFUpload"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn 
                      v-if="pdfUrl"
                      color="primary" 
                      @click="viewPDF"
                      class="mr-4 nexus-btn"
                    >
                      <v-icon left>mdi-eye</v-icon>View PDF
                    </v-btn>
                    <span v-if="pdfUrl" class="success--text">
                      <v-icon color="success" class="mr-1">mdi-check-circle</v-icon>
                      PDF Uploaded
                    </span>
                    <span v-else class="grey--text">No PDF uploaded</span>
                  </v-col>
                </v-row>

                <!-- Procurement Updates Section -->
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">PROCUREMENT UPDATES</h3>
                      <div class="section-divider"></div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-textarea
                      v-model="latestUpdate" 
                      label="Add Procurement Update" 
                      outlined dense
                      prepend-inner-icon="mdi-comment-text-outline" 
                      rows="3" auto-grow
                      :disabled="lockAll || userRole === 'Salesperson'"
                      :placeholder="lockAll ? 'Updates locked - SO is complete' : (userRole === 'Salesperson' ? 'View only - updates managed by Procurement' : 'Enter update for Sales team...')"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center">
                    <v-btn 
                      v-if="userRole !== 'Salesperson'"
                      color="primary" 
                      @click="addUpdate"
                      :disabled="!latestUpdate || lockAll"
                      class="nexus-btn"
                    >
                      <v-icon left>mdi-plus</v-icon>Add Update
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row justify="center" class="my-6">
                  <v-col cols="auto">
                    <v-btn
                      v-if="userRole !== 'Salesperson'"
                      large 
                      color="primary"
                      class="nexus-btn mr-4"
                      @click="confirmSubmit"
                      :disabled="!valid || loading || lockAll || saving"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      {{ saving ? 'Saving...' : 'Save Changes' }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      large
                      outlined
                      color="primary"
                      class="nexus-btn-outline mr-4"
                      @click="viewLinkedShipments"
                    >
                      <v-icon left>mdi-truck</v-icon>View Linked Shipments
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      large
                      outlined
                      color="success"
                      class="nexus-btn-outline"
                      @click="manageShipmentAssignment"
                      :disabled="lockAll"
                    >
                      <v-icon left>mdi-link</v-icon>Manage Shipments
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>

              <!-- Combined Updates Table -->
              <v-row v-if="combinedUpdates.length" class="mt-6">
                <v-col cols="12">
                  <div class="section-header mb-4">
                    <h3 class="section-title">UPDATE HISTORY</h3>
                    <div class="section-divider"></div>
                  </div>
                  <v-data-table
                    :headers="tableHeaders" 
                    :items="combinedUpdates" 
                    sort-by="timestamp" 
                    sort-desc
                    class="nexus-table"
                  >
                    <template #item.timestamp="{ item }">
                      {{ formatTimestamp(item.timestamp) }}
                    </template>
                    <template #item.source="{ item }">
                      <v-chip
                        :color="item.source === 'SO' ? 'primary' : 'orange'"
                        dark
                        small
                        label
                      >
                        {{ item.source }}
                      </v-chip>
                    </template>
                    <template #item.shipmentNumber="{ item }">
                      <span v-if="item.shipmentNumber">
                        {{ item.shipmentNumber === 'OPEN_ORDER' ? 'Open Order' : `#${item.shipmentNumber}` }}
                      </span>
                      <span v-else class="grey--text">-</span>
                    </template>
                    <template #item.actions="{ item }">
                      <v-btn
                        v-if="(userRole === 'Admin' || userRole === 'Procurement') && item.source === 'SO' && item.originalIndex !== null && !lockAll" 
                        icon color="error"
                        @click="removeUpdate(item.originalIndex)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <v-tooltip v-else-if="lockAll && item.source === 'SO' && item.originalIndex !== null" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon 
                            color="grey" 
                            v-bind="attrs" 
                            v-on="on"
                          >
                            mdi-lock
                          </v-icon>
                        </template>
                        <span>Updates locked - SO is complete</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Shipment Assignment Management Dialog -->
    <v-dialog v-model="shipmentManagementDialog.visible" max-width="900">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          Manage Shipment Assignment for SO #{{ salesOrderNumber }}
          <v-spacer />
          <v-btn icon @click="shipmentManagementDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="mb-4">
            <h3 class="mb-3" style="color: #475569;">Current Assignments</h3>
            <v-alert 
              v-if="lockAll" 
              type="warning" 
              dense 
              outlined 
              class="mb-4"
            >
              <v-icon slot="prepend">mdi-lock</v-icon>
              Shipment management is locked - SO is complete
            </v-alert>
            <div v-if="shipmentManagementDialog.currentAssignments.length === 0" class="text-center py-4">
              <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-package-variant</v-icon>
              <p class="grey--text">This SO is only assigned to the default Open Order</p>
            </div>
            <v-row v-else>
              <v-col 
                v-for="assignment in shipmentManagementDialog.currentAssignments" 
                :key="assignment.id"
                cols="12" md="6"
              >
                <v-card outlined class="assignment-card">
                  <v-card-title class="assignment-card-title">
                    <v-icon left :color="assignment.shipmentNumber === 'OPEN_ORDER' ? 'indigo' : 'primary'">
                      mdi-truck
                    </v-icon>
                    {{ assignment.shipmentNumber === 'OPEN_ORDER' ? 'Open Order (Default)' : `Shipment #${assignment.shipmentNumber}` }}
                  </v-card-title>
                  <v-card-subtitle>
                    Status: {{ assignment.status }}<br>
                    Expected Arrival: {{ assignment.expectedArrivalDate || 'N/A' }}
                  </v-card-subtitle>
                  <v-card-actions>
                    <v-btn 
                      v-if="(!lockAll) && (assignment.shipmentNumber !== 'OPEN_ORDER' || shipmentManagementDialog.currentAssignments.filter(a => a.shipmentNumber !== 'OPEN_ORDER').length > 0)"
                      color="error" 
                      text 
                      @click="removeFromShipment(assignment)"
                      :loading="shipmentManagementDialog.removing === assignment.id"
                    >
                      <v-icon left>mdi-minus</v-icon>Remove from Shipment
                    </v-btn>
                    <v-chip 
                      v-else-if="lockAll"
                      color="grey" 
                      dark 
                      small
                    >
                      <v-icon left small>mdi-lock</v-icon>Locked - Complete
                    </v-chip>
                    <v-chip 
                      v-else-if="assignment.shipmentNumber === 'OPEN_ORDER'"
                      color="indigo" 
                      dark 
                      small
                    >
                      Cannot Remove - Only Assignment
                    </v-chip>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-6"></v-divider>

          <div>
            <h3 class="mb-3" style="color: #475569;">Add to Additional Shipments</h3>
            <p class="mb-4 grey--text">Select shipments to assign this SO to (for partial fulfillment scenarios)</p>
            
            <v-row>
              <v-col cols="12" md="8">
                <v-autocomplete
                  v-model="shipmentManagementDialog.selectedShipment"
                  :items="shipmentManagementDialog.availableShipments"
                  :item-text="item => item.shipmentNumber === 'OPEN_ORDER' ? 'Open Order (Default)' : `Shipment #${item.shipmentNumber} - ${item.supplierName || 'No Supplier'}`"
                  item-value="id"
                  label="Select Shipment to Add"
                  dense outlined
                  prepend-inner-icon="mdi-truck-plus"
                  class="nexus-input"
                  clearable
                  :disabled="shipmentManagementDialog.availableShipments.length === 0"
                  :placeholder="shipmentManagementDialog.availableShipments.length === 0 ? 'No additional shipments available' : 'Choose a shipment...'"
                >
                  <template #item="{ item }">
                    <v-list-item-icon>
                      <v-icon :color="item.shipmentNumber === 'OPEN_ORDER' ? 'indigo' : 'primary'">
                        mdi-truck
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.shipmentNumber === 'OPEN_ORDER' ? 'Open Order (Default)' : `Shipment #${item.shipmentNumber}` }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.supplierName || 'No Supplier' }} â€¢ {{ item.status }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="success"
                  :disabled="!shipmentManagementDialog.selectedShipment || lockAll"
                  @click="addToShipment"
                  :loading="shipmentManagementDialog.adding"
                  class="nexus-btn"
                >
                  <v-icon left>mdi-plus</v-icon>Add to Shipment
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Linked Shipments Dialog -->
    <v-dialog v-model="shipmentsDialog.visible" max-width="800">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          Linked Shipments for SO #{{ salesOrderNumber }}
          <v-spacer />
          <v-btn icon @click="shipmentsDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row v-if="shipmentsDialog.loading" justify="center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-row>
          <v-row v-else>
            <v-col
              v-for="shipment in shipmentsDialog.shipments"
              :key="shipment.shipmentNumber"
              cols="12"
              md="6"
            >
              <v-card class="shipment-card mb-2" outlined @click="openShipment(shipment)">
                <v-card-title class="shipment-card-title">
                  <v-icon left :color="shipment.shipmentNumber === 'OPEN_ORDER' ? 'indigo' : 'primary'">mdi-truck</v-icon>
                  {{ shipment.shipmentNumber === 'OPEN_ORDER' ? 'Open Order (Default)' : `Shipment #${shipment.shipmentNumber}` }}
                </v-card-title>
                <v-card-subtitle>
                  Status: {{ shipment.status }}<br>
                  Expected Arrival: {{ shipment.expectedArrivalDate || 'N/A' }}
                </v-card-subtitle>
                <v-card-actions>
                  <v-btn 
                    :color="shipment.shipmentNumber === 'OPEN_ORDER' ? 'indigo' : 'primary'" 
                    text
                    class="nexus-btn-text"
                  >
                    <v-icon left>mdi-open-in-new</v-icon>
                    {{ shipment.shipmentNumber === 'OPEN_ORDER' ? 'View Open Orders' : 'View Details' }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-if="!shipmentsDialog.loading && !shipmentsDialog.shipments.length">
            <v-col>
              <p class="text-center grey--text">No linked shipments found for this SO.</p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialogs -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="success" text @click="confirmationDialog.confirm">Yes</v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible=false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="snackbar" color="success" timeout="3000">
      SO Tracker updated successfully!
      <v-btn color="white" text @click="snackbar=false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar=false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, updateDoc,
  getDoc, doc, query, where
} from 'firebase/firestore';
import { 
  getStorage, ref as storageRef, uploadBytes, getDownloadURL 
} from 'firebase/storage';
import { firebaseApp, auth } from '@/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  name:'EditSalesTracker',
  data(){
    return{
      loading:false, valid:false, saving:false,

      salesOrderNumber:'', 
      customerName:'',
      engineer:null, salesperson:null,
      orderPlacedDate:'', 
      method:'',
      status:'Open Order',
      arrivalDate:'', latestUpdate:'', updates:[],
      shipmentUpdates: [],
      
      pdfFile: null,
      pdfUrl: '',

      customerOptions:[], engineerOptions:[], salespersonOptions:[],
      methodOptions: ['Air', 'Sea'],
      statusOptions:[
        'Open Order', 'Referenced', 'Booked', 'Manufacturing', 
        'Partial Supply', 'In Transit', 'Docked', 'At Airport', 
        'On Route to JHB', 'Complete', 'Delivered'
      ],

      orderDateMenu:false, arrivalDateMenu:false,
      selectedTrackerId:null,
      loggedInUser:'', userRole:'',
      originalStatus: '',

      confirmationDialog:{ visible:false,title:'',message:'',confirm:null },
      snackbar:false, errorSnackbar:false, errorMessage:'',
      
      pdfDialog: {
        visible: false,
        url: ""
      },
      
      shipmentsDialog: {
        visible: false,
        shipments: [],
        loading: false
      },

      shipmentManagementDialog: {
        visible: false,
        currentAssignments: [],
        availableShipments: [],
        selectedShipment: null,
        adding: false,
        removing: null
      },

      rules:{ required:v=>!!v||'Required.' },
      tableHeaders:[
        { text:'Date & Time', value:'timestamp' },
        { text:'Source', value:'source' },
        { text:'Shipment', value:'shipmentNumber' },
        { text:'Update', value:'text' },
        { text:'User', value:'user' },
        { text:'', value:'actions', sortable:false }
      ],
    };
  },

  computed:{
    lockAll(){ return this.originalStatus==='Complete' || this.originalStatus==='Delivered'; },
    isBeingSetToComplete() { return this.status === 'Complete' || this.status === 'Delivered'; },
    combinedUpdates() {
      // Process SO updates - check if they're actually shipment status updates
      const soUpdates = this.updates.map((update, index) => {
        // Check if this is an automatic status update from a shipment
        const isShipmentStatusUpdate = update.text && (
          update.text.includes('Status updated to') && 
          update.text.includes('from Shipment')
        );
        
        // Extract shipment number if it's a shipment status update
        let extractedShipmentNumber = null;
        if (isShipmentStatusUpdate) {
          const match = update.text.match(/from Shipment #?(\w+)/);
          extractedShipmentNumber = match ? match[1] : null;
        }
        
        return {
          ...update,
          source: isShipmentStatusUpdate ? 'Shipment' : 'SO',
          shipmentNumber: isShipmentStatusUpdate ? extractedShipmentNumber : null,
          originalIndex: isShipmentStatusUpdate ? null : index,
          timestamp: this.parseTimestamp(update.timestamp)
        };
      });
      
      // Process actual shipment updates
      const shipmentUpdates = this.shipmentUpdates.map(update => ({
        ...update,
        source: 'Shipment',
        originalIndex: null,
        timestamp: this.parseTimestamp(update.timestamp)
      }));
      
      return [...soUpdates, ...shipmentUpdates]
        .sort((a, b) => b.timestamp - a.timestamp);
    }
  },

  async created(){
    this.loading=true;
    this.selectedTrackerId = localStorage.getItem('selectedSalesTrackerId') || '';

    onAuthStateChanged(auth, async user=>{
      if(user){
        this.loggedInUser=user.email;
        try{
          const uSnap=await getDoc(doc(db,'users',user.uid));
          this.userRole=uSnap.exists() ? uSnap.data().role || '' : '';
        }catch{ this.userRole=''; }
      }else{ this.loggedInUser='Guest'; }
    });

    await Promise.all([
      this.fetchCustomerOptions(),
      this.fetchEngineerOptions(),
      this.fetchSalespersonOptions()
    ]);

    if(this.selectedTrackerId) {
      await this.loadSalesTracker();
      await this.loadShipmentUpdates();
    }
    this.loading=false;
  },

  methods:{
    async fetchCustomerOptions() {
      try {
        const snap = await getDocs(collection(db, 'customers'));
        this.customerOptions = snap.docs.map(doc => ({
          docId: doc.id,
          companyName: doc.data()['Company Name'] || doc.data().customerName || '',
          contactPerson: doc.data()['Contact Person'] || '',
          customerAccount: doc.data()['Customer Account'] || ''
        })).filter(o => o.companyName);
      } catch (e) { 
        this.showError('Failed to fetch customers.'); 
      }
    },
    
    async fetchEngineerOptions(){
      try{
        const q=query(collection(db,'users'), where('role','==','Engineer'));
        const snap=await getDocs(q);
        this.engineerOptions=snap.docs.map(d=>({ uid:d.id, fullName:d.data().fullName }));
      }catch(e){ this.showError('Failed to fetch engineers.'); }
    },
    
    async fetchSalespersonOptions(){
      try{
        const q=query(collection(db,'users'), where('role','==','Salesperson'));
        const snap=await getDocs(q);
        this.salespersonOptions=snap.docs.map(d=>({ uid:d.id, fullName:d.data().fullName }));
      }catch(e){ this.showError('Failed to fetch salespersons.'); }
    },

    async loadSalesTracker(){
      try{
        const d=await getDoc(doc(db,'SalesTracker',this.selectedTrackerId));
        if(!d.exists()) return this.showError('SO Tracker not found.');
        const data=d.data();
        
        // Ensure customer options are loaded before processing customer data
        if (this.customerOptions.length === 0) {
          await this.fetchCustomerOptions();
        }
        
        // If customerName is stored as company name, find the matching docId
        let customerValue = data.customerName || '';
        if (customerValue && this.customerOptions.length > 0) {
          const matchingCustomer = this.customerOptions.find(c => c.companyName === customerValue);
          customerValue = matchingCustomer ? matchingCustomer.docId : customerValue;
        }
        
        Object.assign(this,{
          salesOrderNumber:data.salesOrderNumber||'',
          customerName: customerValue,
          engineer:data.engineer||null, salesperson:data.salesperson||null,
          orderPlacedDate:data.orderPlacedDate||'',
          method:data.method||'',
          status:data.status||'',
          originalStatus:data.status||'',
          arrivalDate:data.arrivalDate||'', pdfUrl:data.pdfUrl||'',
          updates:(data.supplierUpdates||[]).map(u=>({...u, timestamp:this.parseTimestamp(u.timestamp)}))
                                           .sort((a,b)=>b.timestamp-a.timestamp)
        });
      }catch(e){ this.showError('Failed to load SO tracker.'); }
    },

    async loadShipmentUpdates() {
      try {
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        const updates = [];
        
        querySnapshot.forEach(shipmentDoc => {
          const data = shipmentDoc.data();
          const linkedSONumbers = data.linkedSONumbers || [];
          
          if (linkedSONumbers.includes(this.salesOrderNumber)) {
            const shipmentUpdates = data.latestUpdateFromSupplier || [];
            shipmentUpdates.forEach(update => {
              updates.push({
                ...update,
                shipmentNumber: data.shipmentNumber,
                timestamp: this.parseTimestamp(update.timestamp)
              });
            });
          }
        });
        
        this.shipmentUpdates = updates.sort((a, b) => b.timestamp - a.timestamp);
      } catch (error) {
        this.showError('Failed to load shipment updates.');
      }
    },

    parseTimestamp(timestamp) {
      // Handle Firestore timestamp objects
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        return timestamp.seconds * 1000;
      }
      // Handle number timestamps
      if (typeof timestamp === 'number') return timestamp;
      // Handle string timestamps
      if (typeof timestamp === 'string') {
        const parsed = Date.parse(timestamp);
        return isNaN(parsed) ? Date.now() : parsed;
      }
      // Default fallback
      return Date.now();
    },

    formatTimestamp(timestamp) {
      try {
        const parsedTimestamp = this.parseTimestamp(timestamp);
        const date = new Date(parsedTimestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleString('en-ZA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return 'Invalid Date';
      }
    },

    async handlePDFUpload() {
      if (!this.pdfFile) return;
      
      try {
        this.loading = true;
        const fileName = `so_pdfs/${this.salesOrderNumber}_${Date.now()}.pdf`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, this.pdfFile);
        this.pdfUrl = await getDownloadURL(fileRef);
        
        this.showSuccess('PDF uploaded successfully!');
      } catch (error) {
        this.showError('Failed to upload PDF.');
      } finally {
        this.loading = false;
      }
    },

    viewPDF() {
      if (this.pdfUrl) {
        window.open(this.pdfUrl, '_blank');
      }
    },

    async viewLinkedShipments() {
      this.shipmentsDialog.visible = true;
      this.shipmentsDialog.loading = true;
      this.shipmentsDialog.shipments = [];

      try {
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        const linkedShipments = [];
        
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const linkedSONumbers = data.linkedSONumbers || [];
          if (linkedSONumbers.includes(this.salesOrderNumber)) {
            linkedShipments.push({
              id: doc.id,
              shipmentNumber: data.shipmentNumber,
              status: data.status,
              expectedArrivalDate: data.expectedArrivalDate,
              supplierName: data.supplierName
            });
          }
        });
        
        // Sort with Open Order first, then by shipment number
        this.shipmentsDialog.shipments = linkedShipments.sort((a, b) => {
          if (a.shipmentNumber === 'OPEN_ORDER') return -1;
          if (b.shipmentNumber === 'OPEN_ORDER') return 1;
          return parseInt(a.shipmentNumber) - parseInt(b.shipmentNumber);
        });
      } catch (error) {
        this.showError("Failed to load linked shipments.");
      } finally {
        this.shipmentsDialog.loading = false;
      }
    },

    openShipment(shipment) {
      localStorage.setItem('selectedShipmentId', shipment.id);
      this.$router.push({ name: 'EditShipment' });
    },

    async manageShipmentAssignment() {
      this.shipmentManagementDialog.visible = true;
      await this.loadShipmentAssignmentData();
    },

    async loadShipmentAssignmentData() {
      try {
        // Load all shipments
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        const allShipments = [];
        const currentAssignments = [];
        
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const shipment = {
            id: doc.id,
            shipmentNumber: data.shipmentNumber,
            status: data.status,
            expectedArrivalDate: data.expectedArrivalDate,
            supplierName: data.supplierName,
            linkedSONumbers: data.linkedSONumbers || []
          };
          
          allShipments.push(shipment);
          
          // Check if this SO is assigned to this shipment
          if (shipment.linkedSONumbers.includes(this.salesOrderNumber)) {
            currentAssignments.push(shipment);
          }
        });
        
        // Available shipments are those not currently assigned
        const assignedShipmentIds = currentAssignments.map(s => s.id);
        const availableShipments = allShipments.filter(s => !assignedShipmentIds.includes(s.id));
        
        this.shipmentManagementDialog.currentAssignments = currentAssignments;
        this.shipmentManagementDialog.availableShipments = availableShipments;
        this.shipmentManagementDialog.selectedShipment = null;
        
      } catch (error) {
        this.showError('Failed to load shipment data.');
      }
    },

    async addToShipment() {
      if (!this.shipmentManagementDialog.selectedShipment) return;
      
      this.shipmentManagementDialog.adding = true;
      
      try {
        const shipmentRef = doc(db, 'shipments', this.shipmentManagementDialog.selectedShipment);
        const shipmentDoc = await getDoc(shipmentRef);
        
        if (shipmentDoc.exists()) {
          const data = shipmentDoc.data();
          const currentSONumbers = data.linkedSONumbers || [];
          
          // Add SO number if not already present
          if (!currentSONumbers.includes(this.salesOrderNumber)) {
            const updatedSONumbers = [...currentSONumbers, this.salesOrderNumber];
            
            await updateDoc(shipmentRef, {
              linkedSONumbers: updatedSONumbers
            });
            
            this.showSuccess(`SO #${this.salesOrderNumber} added to shipment successfully!`);
            await this.loadShipmentAssignmentData(); // Refresh the data
          } else {
            this.showError('SO is already assigned to this shipment.');
          }
        }
      } catch (error) {
        this.showError('Failed to add SO to shipment.');
      } finally {
        this.shipmentManagementDialog.adding = false;
      }
    },

    async removeFromShipment(shipment) {
      // Prevent any modifications if SO is complete
      if (this.lockAll) {
        this.showError('Cannot modify shipment assignments - SO is complete.');
        return;
      }
      
      // Check if removing Open Order when other shipments exist
      if (shipment.shipmentNumber === 'OPEN_ORDER') {
        const otherShipments = this.shipmentManagementDialog.currentAssignments.filter(
          s => s.shipmentNumber !== 'OPEN_ORDER'
        );
        if (otherShipments.length === 0) {
          this.showError('Cannot remove SO from Open Order when no other shipments are assigned.');
          return;
        }
      }
      
      this.shipmentManagementDialog.removing = shipment.id;
      
      try {
        const shipmentRef = doc(db, 'shipments', shipment.id);
        const shipmentDoc = await getDoc(shipmentRef);
        
        if (shipmentDoc.exists()) {
          const data = shipmentDoc.data();
          const currentSONumbers = data.linkedSONumbers || [];
          
          // Remove SO number
          const updatedSONumbers = currentSONumbers.filter(so => so !== this.salesOrderNumber);
          
          await updateDoc(shipmentRef, {
            linkedSONumbers: updatedSONumbers
          });
          
          // If removing a non-Open Order shipment, check if we need to fallback to Open Order
          if (shipment.shipmentNumber !== 'OPEN_ORDER') {
            await this.ensureOpenOrderFallback();
          }
          
          // Update SO status based on remaining shipments
          await this.updateSOStatusBasedOnShipments();
          
          this.showSuccess(`SO #${this.salesOrderNumber} removed from shipment successfully!`);
          await this.loadShipmentAssignmentData(); // Refresh the data
        }
      } catch (error) {
        this.showError('Failed to remove SO from shipment.');
      } finally {
        this.shipmentManagementDialog.removing = null;
      }
    },

    async ensureOpenOrderFallback() {
      try {
        // Check if SO is still assigned to any shipments
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        let hasAssignments = false;
        
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const linkedSONumbers = data.linkedSONumbers || [];
          if (linkedSONumbers.includes(this.salesOrderNumber)) {
            hasAssignments = true;
          }
        });
        
        // If no assignments found, add back to Open Order
        if (!hasAssignments) {
          const openOrderQuery = query(
            collection(db, "shipments"),
            where("shipmentNumber", "==", "OPEN_ORDER")
          );
          const openOrderSnapshot = await getDocs(openOrderQuery);
          
          if (!openOrderSnapshot.empty) {
            const openOrderDoc = openOrderSnapshot.docs[0];
            const openOrderData = openOrderDoc.data();
            const currentSONumbers = openOrderData.linkedSONumbers || [];
            
            if (!currentSONumbers.includes(this.salesOrderNumber)) {
              await updateDoc(openOrderDoc.ref, {
                linkedSONumbers: [...currentSONumbers, this.salesOrderNumber]
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to ensure Open Order fallback:', error);
      }
    },

    async updateSOStatusBasedOnShipments() {
      try {
        // Get all shipments this SO is linked to
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        const linkedShipments = [];
        
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const linkedSONumbers = data.linkedSONumbers || [];
          if (linkedSONumbers.includes(this.salesOrderNumber)) {
            linkedShipments.push({
              id: doc.id,
              shipmentNumber: data.shipmentNumber,
              status: data.status
            });
          }
        });
        
        if (linkedShipments.length === 0) return;
        
        // Determine new status based on shipment statuses
        const completedShipments = linkedShipments.filter(s => s.status === 'Complete');
        const hasOpenOrder = linkedShipments.some(s => s.shipmentNumber === 'OPEN_ORDER');
        
        let newStatus = this.status;
        
        if (linkedShipments.length === 1) {
          // Single shipment - follow its status
          const singleShipment = linkedShipments[0];
          if (singleShipment.status === 'Complete') {
            newStatus = 'Complete';
          }
        } else {
          // Multiple shipments - check for partial or complete
          if (completedShipments.length > 0 && completedShipments.length < linkedShipments.length) {
            // Some completed, some not - partial supply
            newStatus = 'Partial Supply';
          } else if (completedShipments.length === linkedShipments.length) {
            // All completed
            newStatus = 'Complete';
          }
        }
        
        // Update SO status if changed
        if (newStatus !== this.status) {
          this.status = newStatus;
          await this.submitForm();
        }
      } catch (error) {
        console.error('Failed to update SO status:', error);
      }
    },

    addUpdate(){
      if(!this.latestUpdate) return;
      this.updates.unshift({ 
        timestamp:Date.now(), 
        text:this.latestUpdate, 
        user:this.loggedInUser 
      });
      this.latestUpdate='';
    },
    
    removeUpdate(i){ this.updates.splice(i,1); },

    confirmSubmit(){
      this.confirmationDialog={
        visible:true, title:'Confirm Save',
        message:'Save changes to this SO tracker?', confirm:this.submitForm
      };
    },
    
    async submitForm(){
      if(!this.$refs.form.validate() || this.saving) return;
      
      this.saving = true;
      this.loading = true;
      
      try{
        // Find the selected customer to get company name for storage
        const selectedCustomer = this.customerOptions.find(
          customer => customer.docId === this.customerName
        );
        
        // Create the update object with explicit null/empty fallbacks
        const updateData = {
          salesOrderNumber: this.salesOrderNumber || '',
          customerName: selectedCustomer ? selectedCustomer.companyName : (this.customerName || ''),
          engineer: this.engineer || null,
          salesperson: this.salesperson || null,
          orderPlacedDate: this.orderPlacedDate || '',
          method: this.method || '',
          status: this.status || '',
          arrivalDate: this.arrivalDate || '',
          pdfUrl: this.pdfUrl || '',
          supplierUpdates: this.updates || []
        };

        // Use JSON serialization to remove undefined values completely
        const cleanedData = JSON.parse(JSON.stringify(updateData));
        
        await updateDoc(doc(db,'SalesTracker',this.selectedTrackerId), cleanedData);
        this.originalStatus = this.status;
        this.snackbar=true;
      }catch(e){ 
        this.showError(`Failed to update SO tracker: ${e.message}`); 
      }
      finally{ 
        this.loading = false; 
        this.saving = false;
        this.confirmationDialog.visible = false; 
      }
    },

    confirmBackNavigation(){
      this.confirmationDialog={
        visible:true,title:'Confirm Navigation',
        message:'Go back and discard changes?', confirm:this.goBack
      };
    },
    
    goBack(){ 
      this.confirmationDialog.visible=false; 
      this.$router.go(-1); 
    },

    showError(msg){ 
      this.errorMessage=msg; 
      this.errorSnackbar=true; 
    },
    
    showSuccess(msg) {
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-outline {
  border: 2px solid #475569 !important;
  color: #475569 !important;
  background: transparent !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-outline:hover {
  background: #475569 !important;
  color: white !important;
  transform: translateY(-2px) !important;
}

.nexus-btn-text {
  color: #475569 !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.nexus-btn-text:hover {
  background: rgba(71, 85, 105, 0.1) !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.nexus-table {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
}

.nexus-table >>> .v-data-table__wrapper {
  border-radius: 12px !important;
}

.nexus-table >>> th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.shipment-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.shipment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.15) !important;
}

.shipment-card-title {
  color: #1e293b !important;
  font-weight: 600 !important;
}

.shipment-card-title:hover {
  color: #475569 !important;
}

/* Assignment Management Styles */
.assignment-card {
  transition: all 0.3s ease;
  border-radius: 12px !important;
  border: 2px solid #e2e8f0 !important;
}

.assignment-card:hover {
  border-color: #475569 !important;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.15) !important;
}

.assignment-card-title {
  color: #1e293b !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  padding-bottom: 8px !important;
}

.assignment-card-title .v-icon {
  margin-right: 8px !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Loading overlay */
.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>